import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { InverterStatsService, InverterStat, EChartsInverterData } from '../../services/inverter-stats.service';
import { Subscription } from 'rxjs';

// ECharts imports
import { NgxEchartsModule } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

// Minimum date constant - January 1, 2024
const MIN_DATE = '2024-01-01';

enum DateRangePreset {
  CurrentMonth = 'currentMonth',
  LastMonth = 'lastMonth',
  Last3Months = 'last3Months',
  Custom = 'custom'
}

interface DateRangeOption {
  value: DateRangePreset;
  label: string;
}

@Component({
  selector: 'app-inverter-stats-graph',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxEchartsModule],
  templateUrl: './inverter-stats-graph.component.html',
  styleUrl: './inverter-stats-graph.component.css'
})
export class InverterStatsGraphComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  // Chart options and data
  chartOption: EChartsOption = {};
  echartsInstance: any;
  chartData: EChartsInverterData = { xAxis: [], series: [], timestamps: [] };

  // Statistics
  totalOnHours = '';
  totalOffHours = '';
  
  // Date range presets
  dateRangePresets: DateRangeOption[] = [
    { value: DateRangePreset.CurrentMonth, label: 'Current Month' },
    { value: DateRangePreset.LastMonth, label: 'Last Month' },
    { value: DateRangePreset.Last3Months, label: 'Last 3 Months' },
    { value: DateRangePreset.Custom, label: 'Custom Range' }
  ];
  
  // Date range form
  dateRangeForm = new FormGroup({
    preset: new FormControl<DateRangePreset>(DateRangePreset.CurrentMonth),
    startDate: new FormControl<string>(this.getDefaultStartDate()),
    endDate: new FormControl<string>(this.getDefaultEndDate()),
    startTime: new FormControl<string>('00:00'),
    endTime: new FormControl<string>('23:59'),
    realtime: new FormControl<boolean>(false),
    aggregation: new FormControl<'minute' | 'hour' | 'day'>('minute')
  });

  // UI states
  loading = false;
  error: string | null = null;
  isInitialized = false;
  showControls = true;
  
  // Aggregation options
  aggregationOptions = [
    { value: 'minute', label: 'Minute' },
    { value: 'hour', label: 'Hour' },
    { value: 'day', label: 'Day' }
  ];
  
  private subscriptions: Subscription[] = [];
  private realtimeSubscription: Subscription | null = null;
  private startDateSubscription: Subscription | null = null;
  
  /**
   * Custom validator to enforce minimum date constraint
   * @param minDate - Minimum date in YYYY-MM-DD format
   * @returns Validator function
   */
  private minDateValidator(minDate: string) {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (!control.value) {
        return null; // Don't validate empty values
      }
      
      const inputDate = new Date(control.value);
      const minimumDate = new Date(minDate);
      
      if (inputDate < minimumDate) {
        return { 'minDate': { 'requiredDate': minDate, 'actualDate': control.value } };
      }
      
      return null;
    };
  }
  
  constructor(private inverterStatsService: InverterStatsService) {
    // Add validation on startDate changes
    const startDateControl = this.dateRangeForm.get('startDate');
    if (startDateControl) {
      this.startDateSubscription = startDateControl.valueChanges.subscribe(startDate => {
        if (startDate) {
          // Validate that the date is not before our minimum date
          const selectedDate = new Date(startDate);
          const minDate = new Date(MIN_DATE);
          
          if (selectedDate < minDate) {
            // Reset to minimum date
            this.dateRangeForm.get('startDate')?.setValue(MIN_DATE, { emitEvent: false });
            
            // Show error message
            this.error = `Start date cannot be before ${MIN_DATE}. Adjusted to minimum allowed date.`;
            
            // Clear error after a short delay
            setTimeout(() => {
              if (this.error === `Start date cannot be before ${MIN_DATE}. Adjusted to minimum allowed date.`) {
                this.error = null;
              }
            }, 5000);
          }
        }
      });
    }
  }
  
  ngOnInit(): void {
    // Subscribe to loading and error states from service
    this.subscriptions.push(
      this.inverterStatsService.loading$.subscribe(loading => {
        this.loading = loading;
      })
    );
    
    this.subscriptions.push(
      this.inverterStatsService.error$.subscribe(error => {
        this.error = error;
      })
    );

    // Add validators to the date fields
    this.dateRangeForm.get('startDate')?.addValidators(this.minDateValidator(MIN_DATE));
    this.dateRangeForm.get('endDate')?.addValidators(this.minDateValidator(MIN_DATE));

    // Single subscription to handle preset changes and form state
    this.subscriptions.push(
      this.dateRangeForm.get('preset')?.valueChanges.subscribe(preset => {
        if (preset) {
          if (preset !== DateRangePreset.Custom) {
            // First update the date range
            const { startDate, endDate } = this.calculateDateRange(preset);
            
            // Update form values
            this.dateRangeForm.patchValue({
              startDate,
              endDate
            }, { emitEvent: true });

            // Then disable the inputs
            this.dateRangeForm.get('startDate')?.disable();
            this.dateRangeForm.get('endDate')?.disable();
            this.dateRangeForm.get('startTime')?.disable();
            this.dateRangeForm.get('endTime')?.disable();
          } else {
            // Enable date inputs for custom range
            this.dateRangeForm.get('startDate')?.enable();
            this.dateRangeForm.get('endDate')?.enable();
            this.dateRangeForm.get('startTime')?.enable();
            this.dateRangeForm.get('endTime')?.enable();
            
            // Validate custom date range when enabling inputs
            this.validateCustomDateRange();
          }
        }
      }) || new Subscription()
    );
    
    // Setup form value changes to trigger data fetch
    this.subscriptions.push(
      this.dateRangeForm.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev) === JSON.stringify(curr);
        }),
        tap(() => {
          // Cancel any existing realtime subscription when form values change
          this.cancelRealtimeUpdates();
        }),
        switchMap(() => {
          const { startTimestamp, endTimestamp } = this.getTimestampRange();
          // If realtime is enabled, use realtime data stream
          if (this.dateRangeForm.value.realtime) {
            return this.enableRealtimeUpdates(startTimestamp, endTimestamp);
          } else {
            // Otherwise fetch data once
            return this.inverterStatsService.getInverterStatsByDateRange(startTimestamp, endTimestamp);
          }
        })
      ).subscribe({
        next: (data) => this.updateChartWithData(data),
        error: (err) => console.error('Error fetching data:', err)
      })
    );

    // Set initial date range based on current month
    const { startDate, endDate } = this.calculateDateRange(DateRangePreset.CurrentMonth);
        
    // Update form with initial values and disable inputs
    this.dateRangeForm.patchValue({
      preset: DateRangePreset.CurrentMonth,
      startDate,
      endDate
    });
    
    // Disable the date inputs since we're using a preset
    this.dateRangeForm.get('startDate')?.disable();
    this.dateRangeForm.get('endDate')?.disable();
    this.dateRangeForm.get('startTime')?.disable();
    this.dateRangeForm.get('endTime')?.disable();
  }
  
  ngAfterViewInit(): void {
    this.initializeChart();
  }
  
  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.cancelRealtimeUpdates();
    if (this.startDateSubscription) {
      this.startDateSubscription.unsubscribe();
      this.startDateSubscription = null;
    }
  }
  
  onChartInit(ec: any): void {
    this.echartsInstance = ec;
    this.isInitialized = true;
    
    // Handle resize
    window.addEventListener('resize', () => {
      if (this.echartsInstance) {
        this.echartsInstance.resize();
      }
    });
  }
   refreshData(): void {
    // Validate custom date range first
    this.validateCustomDateRange();
    
    // Get current timestamp range
    const { startTimestamp, endTimestamp } = this.getTimestampRange();
    
    // Cancel any existing realtime updates
    this.cancelRealtimeUpdates();
    
    // If realtime is enabled, restart realtime updates
    if (this.dateRangeForm.value.realtime) {
      this.enableRealtimeUpdates(startTimestamp, endTimestamp);
    }
    
    // Fetch fresh data
    this.inverterStatsService.getInverterStatsByDateRange(startTimestamp, endTimestamp)
      .subscribe({
        next: (data) => this.updateChartWithData(data),
        error: (err) => console.error('Error refreshing data:', err)
      });
  }

  toggleControls(): void {
    this.showControls = !this.showControls;
  }
  
  toggleRealtime(): void {
    const realtimeValue = this.dateRangeForm.get('realtime')?.value;
    this.dateRangeForm.get('realtime')?.setValue(!realtimeValue);
  }
  
  setZoomLevel(level: 'minute' | 'hour' | 'day'): void {
    this.dateRangeForm.get('aggregation')?.setValue(level);
  }
  
  private getDefaultStartDate(): string {
    const now = new Date();
    // Set to first day of current month in local timezone
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    // Check if the first day of month is before our minimum date (Jan 1, 2024)
    const minDate = new Date(MIN_DATE);
    if (firstDayOfMonth < minDate) {
      return MIN_DATE;
    }
    
    // Format as YYYY-MM-DD in local timezone
    return this.formatDateToLocalISODate(firstDayOfMonth);
  }
  
  private getDefaultEndDate(): string {
    const now = new Date();
    // Set to last day of current month in local timezone
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // Format as YYYY-MM-DD in local timezone
    return this.formatDateToLocalISODate(lastDayOfMonth);
  }
  
  private formatDateToLocalISODate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private calculateDateRange(preset: DateRangePreset): { startDate: string, endDate: string } {
    const now = new Date(); // Current local date/time
    let startDate: Date;
    let endDate: Date;
    const minDate = new Date(MIN_DATE); // Minimum allowed date

    switch (preset) {
      case DateRangePreset.CurrentMonth:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = now;
        break;

      case DateRangePreset.LastMonth:
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;

      case DateRangePreset.Last3Months:
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = now;
        break;

      case DateRangePreset.Custom:
        return {
          startDate: this.dateRangeForm.get('startDate')?.value || this.getDefaultStartDate(),
          endDate: this.dateRangeForm.get('endDate')?.value || this.getDefaultEndDate()
        };
    }

    // Enforce minimum date
    if (startDate < minDate) {
      startDate = minDate;
    }

    // Format dates in local timezone instead of using toISOString which uses UTC
    return {
      startDate: this.formatDateToLocalISODate(startDate),
      endDate: this.formatDateToLocalISODate(endDate)
    };
  }

  onPresetChange(preset: DateRangePreset): void {
    // Update preset in the form
    this.dateRangeForm.get('preset')?.setValue(preset);
    
    // If custom, validate the date range
    if (preset === DateRangePreset.Custom) {
      this.validateCustomDateRange();
    }
  }

  private getTimestampRange(): { startTimestamp: number, endTimestamp: number } {
    const formValues = this.dateRangeForm.getRawValue();
    
    const startDate = formValues.startDate || this.getDefaultStartDate();
    const endDate = formValues.endDate || this.getDefaultEndDate();
    const startTime = formValues.startTime || '00:00';
    const endTime = formValues.endTime || '23:59';
    
    let startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    
    // Enforce minimum date
    const minDateTime = new Date(MIN_DATE);
    if (startDateTime < minDateTime) {
      startDateTime = new Date(`${MIN_DATE}T00:00`);
      
      // Update the form if in custom mode
      if (this.dateRangeForm.get('preset')?.value === DateRangePreset.Custom) {
        this.dateRangeForm.patchValue({
          startDate: MIN_DATE,
          startTime: '00:00'
        }, { emitEvent: false });
      }
    }
    
    return {
      startTimestamp: startDateTime.getTime(),
      endTimestamp: endDateTime.getTime()
    };
  }
  
  private updateChartWithData(data: InverterStat[]): void {
    if (!data || data.length === 0) {
      this.error = 'No data available for the selected time range';
      this.totalOnHours = '0 minutes';
      this.totalOffHours = '0 minutes';
      return;
    }
    
    // Clear previous error if successful
    this.error = null;
    
    // Apply aggregation based on selected level
    const aggregation = this.dateRangeForm.value.aggregation || 'hour';
    this.chartData = this.inverterStatsService.getAggregatedData(data, aggregation);
    
    // Calculate total hours for ON and OFF states
    let totalOnTime = 0;
    let totalOffTime = 0;
    
    for (let i = 0; i < this.chartData.series.length; i++) {
      const currValue = this.chartData.series[i];
      
      // Calculate time difference
      let timeDiff: number;
      if (i < this.chartData.series.length - 1) {
        timeDiff = this.chartData.timestamps[i + 1] - this.chartData.timestamps[i];
      } else {
        // For the last point, use the same interval as the previous one
        timeDiff = this.chartData.timestamps[i] - this.chartData.timestamps[i - 1];
      }
      
      // Convert milliseconds to hours
      const hours = timeDiff / (1000 * 60 * 60);
      
      if (currValue === 1) {
        totalOnTime += hours;
      } else {
        totalOffTime += hours;
      }
    }
    
    // Convert hours to days, hours, minutes format
    this.totalOnHours = this.formatTimeAsDaysHoursMinutes(totalOnTime);
    this.totalOffHours = this.formatTimeAsDaysHoursMinutes(totalOffTime);
    
    // Update chart options
    this.updateChartOptions();
    
    // Calculate total ON and OFF hours
    this.calculateTotalOnOffHours(data);
  }
  
  private updateChartOptions(): void {
    this.chartOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const dataIndex = params[0].dataIndex;
          const date = new Date(this.chartData.xAxis[dataIndex]);
          const value = this.chartData.series[dataIndex] === 1 ? 'ON' : 'OFF';
          
          // Guard against invalid date
          if (isNaN(date.getTime())) {
            return `${this.chartData.xAxis[dataIndex]}<br/>Status: <strong>${value}</strong>`;
          }

          // Format the date and time
          const formattedDate = new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).format(date);

          return `<strong>${formattedDate}</strong><br/>Status: <strong>${value}</strong>`;
        }
      },
      xAxis: {
        type: 'category',
        data: this.chartData.xAxis,
        axisLabel: {
          rotate: 45,
          formatter: (value: string) => {
            const aggregation = this.dateRangeForm.value.aggregation;
            const date = new Date(value);
            // Guard against invalid date
            if (isNaN(date.getTime())) {
              return value;
            }
            if (aggregation === 'day') {
              // Show date in format DD/MM/YYYY
              return new Intl.DateTimeFormat('en-GB', { 
                day: '2-digit', 
                month: '2-digit',
                year: 'numeric' 
              }).format(date);
            } else if (aggregation === 'hour') {
              // For hourly, show date and time
              return new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }).format(date);
            } else {
              // For minute, show time
              return new Intl.DateTimeFormat('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }).format(date);
            }
          }
        }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 1,
        interval: 1,
        axisLabel: {
          formatter: (value: number) => value === 1 ? 'ON' : 'OFF'
        }
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          start: 0,
          end: 100
        },
        {
          type: 'inside',
          start: 0,
          end: 100
        }
      ],
      series: [
        {
          name: 'Inverter Status',
          type: 'line',
          step: 'end',
          data: this.chartData.series,
          markLine: {
            symbol: ['none', 'none'],
            label: { show: false },
            lineStyle: { color: '#5470c6' },
            data: this.generateStatusChangeLines()
          }
        }
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      }
    };
    
    if (this.echartsInstance && this.isInitialized) {
      this.echartsInstance.setOption(this.chartOption);
    }
  }
  
  private initializeChart(): void {
    this.chartOption = {
      title: {
        text: 'Inverter Status Over Time',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis'
      },
      xAxis: {
        type: 'category',
        data: []
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 1
      },
      series: [
        {
          type: 'line',
          data: []
        }
      ]
    };
  }
  
  private enableRealtimeUpdates(startTimestamp: number, endTimestamp: number) {
    this.cancelRealtimeUpdates();
    
    this.realtimeSubscription = this.inverterStatsService
      .getRealtimeInverterStats(startTimestamp, endTimestamp)
      .subscribe();
      
    return this.inverterStatsService.getInverterStatsByDateRange(startTimestamp, endTimestamp);
  }
  
  private cancelRealtimeUpdates(): void {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
      this.realtimeSubscription = null;
    }
  }
  
  private generateStatusChangeLines() {
    const markLines = [];
    for (let i = 1; i < this.chartData.series.length; i++) {
      if (this.chartData.series[i] !== this.chartData.series[i-1]) {
        markLines.push({
          xAxis: i
        });
      }
    }
    return markLines;
  }
  
  private calculateTotalOnOffHours(data: InverterStat[]): void {
    let totalOnTime = 0;
    let totalOffTime = 0;
    
    for (let i = 1; i < data.length; i++) {
      const prevStat = data[i - 1];
      const currStat = data[i];
      
      // Calculate the time difference in hours
      const timeDiff = (currStat.timestamp - prevStat.timestamp) / (1000 * 60 * 60);
      
      if (currStat.value === 1) {
        totalOnTime += timeDiff;
      } else {
        totalOffTime += timeDiff;
      }
    }
    
    // Format time as days, hours, minutes
    this.totalOnHours = this.formatTimeAsDaysHoursMinutes(totalOnTime);
    this.totalOffHours = this.formatTimeAsDaysHoursMinutes(totalOffTime);
  }
  
  /**
   * Formats time in hours to a string representation of days, hours, and minutes
   * @param hours Total time in hours
   * @returns Formatted string with days, hours, and minutes on separate lines
   */
  private formatTimeAsDaysHoursMinutes(hours: number): string {
    const totalMinutes = Math.floor(hours * 60);
    const days = Math.floor(totalMinutes / (60 * 24));
    const remainingHours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    
    return `Days: ${days}\nHours: ${remainingHours}\nMinutes: ${minutes}`;
  }

  private validateCustomDateRange(): void {
    // Only validate when custom preset is selected
    if (this.dateRangeForm.get('preset')?.value !== DateRangePreset.Custom) {
      return;
    }
    
    // Get the current start date or use default
    let startDate = this.dateRangeForm.get('startDate')?.value || this.getDefaultStartDate();
    const endDate = this.dateRangeForm.get('endDate')?.value || this.getDefaultEndDate();
    
    // Check if start date is before minimum date
    const minDate = new Date(MIN_DATE);
    const currentStartDate = new Date(startDate);
    
    if (currentStartDate < minDate) {
      // Update to minimum date
      startDate = MIN_DATE;
      this.dateRangeForm.get('startDate')?.setValue(startDate, { emitEvent: false });
      
      // Show error message
      this.error = `Start date cannot be before ${MIN_DATE}. Adjusted to minimum allowed date.`;
      
      // Clear error after a short delay
      setTimeout(() => {
        if (this.error === `Start date cannot be before ${MIN_DATE}. Adjusted to minimum allowed date.`) {
          this.error = null;
        }
      }, 5000);
    }
  }
}